// services/projects.service.ts
import { Pagination } from '../interfaces/pagination';
import { http } from './http.service';
import { parseBlobErrorBody } from '../utils/error-translations';

// ---
export interface Project {
  id: number;
  name: string;
  title: string;
  createdAt?: string;
  currentStageId?: number;
  currentStageName?: string;
  // New: formatted string or array of active stages
  activeStageNames?: string[]; 
  currentStageStartedAt?: string;
  usersCount?: number;
  /** Every stage the server created for the project, open or completed. */
  stages?: ProjectStage[];
  /** Stages 10, 11, 5 and 7 all completed — see isProjectComplete. */
  isCompleted?: boolean;
}

// ---
export interface ProjectStage {
  /** The stage type (1, 2, 3, 5, 7, 8, 9, 10, 11). */
  id?: number;
  name: string;
  startedAt?: string;
  completedAt?: string | null;
  candidates?: number[]; // IDs selected for discussion
}

// ---
export interface ProjectTenant {
  id: number;
  fullName: string;
  firstName?: string;
  lastName?: string;
  idNumber?: string;
  email?: string;
  apartmentNumber?: string;
  phoneNumber?: string;
  signatureStatus: 'signed' | 'not-signed';
  hasSigned?: boolean;
  signedAt?: string | null;
  invitationCode?: string;
  /** For a joined apartment, its member's role; for a free one, how the next member joins. */
  isRepresentative?: boolean;
  /** The user who joined with this apartment; null while it's free. */
  claimedBy?: number | null;
}

/** One apartment of a building (a /residents row), for picking where to move a user. */
export interface ProjectApartment {
  apartmentNumber?: number;
  /** The user who joined with this apartment, if anyone did. */
  claimedBy?: number;
  residentName: string;
}

export interface UserApartment {
  apartmentNumber?: number;
  /** Signed the hasmacha. */
  hasSigned: boolean;
  isRepresentative: boolean;
}

/** One of a project's five documents (GET /projects/{id}/documents — admins read any project's). */
export interface ProjectDocumentInfo {
  documentType: number;
  name: string;
  /** The inspection agreement and the appraiser quote are generated when downloaded. */
  generatedOnDemand: boolean;
  /** False while the building doesn't have it yet (its stage or professional isn't there). */
  isGenerated: boolean;
  /** The copy the representative uploaded, signed by the professional; null when none. */
  signedDocument: { id: number; uploadedAt?: string | null; file?: { name?: string; mimeType?: string } | null } | null;
}

/** GET /projects/{id}/signatures/progress for documents 1–3: the apartments that signed. */
export interface DocumentSignatureProgress {
  documentType: number;
  signedCount: number;
  totalApartments: number;
  /** One decimal, as the server rounds it: show it as sent. */
  signaturePercentage: number;
  thresholdReached: boolean;
}

/** A row of GET /projects/{id}/professionals, with the professional's details (also when hidden or deleted). */
export interface ProjectProfessionalRow {
  professionalId: number;
  /** 0 in discussion, 1 selected, 2 lost its survey. */
  status: number;
  surveyVotes?: number | null;
  selectedAt?: string | null;
  professional?: {
    id: number;
    firstName?: string;
    lastName?: string;
    professionType?: number;
    companyName?: string | null;
    isDisplayed?: boolean;
  } | null;
}

export interface ProjectsResponse extends Pagination<Project> {
  searchTerm?: string;
}

export interface TenantsResponse extends Pagination<ProjectTenant> {
  searchTerm?: string;
}

export interface ProjectsError {
  success: false;
  error: string;
  message: string;
}

interface ProjectsServiceType {
  getAllProjects(): Promise<ProjectsResponse>;
  getProjectById(id: number): Promise<Project | null>;
  deleteProject(id: number): Promise<{ success: true; message: string }>;
  getProjectStages(projectId: number): Promise<ProjectStage[]>;
  getProjectResidents(projectId: number): Promise<TenantsResponse>;
  getUserApartment(projectId: number, userId: number): Promise<UserApartment | null>;
  getProjectApartments(projectId: number): Promise<ProjectApartment[]>;
  downloadHasmachaDocument(projectId: number): Promise<{ blob: Blob; fileName: string }>;
  downloadLawyerSignaturesDocument(projectId: number): Promise<{ blob: Blob; fileName: string }>;
  downloadEntrepreneurSignaturesDocument(projectId: number): Promise<{ blob: Blob; fileName: string }>;
  getProjectDocuments(projectId: number): Promise<ProjectDocumentInfo[]>;
  downloadDocument(projectId: number, documentType: number, format: 'docx' | 'pdf', documentName: string): Promise<{ blob: Blob; fileName: string }>;
  downloadSignedDocument(projectId: number, documentType: number, documentName: string): Promise<{ blob: Blob; fileName: string }>;
  getSignatureProgress(projectId: number, documentType: number): Promise<DocumentSignatureProgress | null>;
  getSelectedProfessionals(projectId: number): Promise<ProjectProfessionalRow[]>;
}

interface ProjectsApiItem {
  id?: number;
  // New fields
  created?: string;
  settlementName?: string;
  street?: string;
  buildingNumber?: string;
  apartmentsCount?: number;
  
  // Single current stage (legacy or primary)
  currentStage?: {
    name?: string;
    type?: number;
    startedAt?: string;
    completedAt?: string | null;
  } | null;

  // New: potentially multiple active stages if backend provides it
  stages?: ProjectStageApiItem[];
}

interface ProjectsApiResponse {
  // New structure
  data?: ProjectsApiItem[];
  meta?: any;
}

interface ProjectStagesApiResponse {
  items?: ProjectStageApiItem[];
  // New structure
  data?: ProjectStageApiItem[];
}

interface ProjectStageApiItem {
  // Legacy
  name?: string;
  // New
  type?: number;
  startedAt?: string;
  completedAt?: string | null;
  candidates?: number[];
}

interface ProjectUsersApiResponse {
  items?: ProjectUserApiItem[];
  // New structure
  data?: ProjectUserApiItem[];
}

interface ProjectUserApiItem {
  id?: number;
  userId?: number;
  firstName?: string;
  lastName?: string;
  idNumber?: string | number | null;
  identityNumber?: string | number | null;
  email?: string | null;
  apartmentNumber?: number | string | null;
  phone?: string | null;
  phoneNumber?: string | null;
  signedAt?: string | null;
  claimed?: string | null;
  /** On /residents rows: the id of the user who joined with this apartment. */
  claimedBy?: number | null;
  hasSigned?: boolean;
  invitationCode?: string;
  isRepresentative?: boolean;
}

const unwrapRows = (payload: any): ProjectUserApiItem[] =>
  payload?.data ?? payload?.items ?? (Array.isArray(payload) ? payload : []);

class ProjectsServiceError extends Error implements ProjectsError {
  success: false = false;
  error: string;
  message: string;
  /** The failed request's response, so screens can read its status and `meta.description`. */
  response?: any;

  constructor(code: string, message: string, response?: any) {
    super(message);
    this.name = 'ProjectsServiceError';
    this.error = code;
    this.message = message;
    this.response = response;
  }
}

const PROJECTS_ENDPOINT = '/Projects';

const coerceNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

/**
 * The stages the server creates (Models/ProjectStage.cs). There are no stages 4 and 6, and 12/13 (the work orders) were
 * removed: selecting the inspector or the appraiser completes their stage.
 */
export const STAGE_NAMES: Record<number, string> = {
  1: 'השגת רוב דיירים',
  2: 'הצגת עורכי דין',
  3: 'בחירת עורך דין',
  5: 'בחירת מפקח',
  7: 'בחירת שמאי',
  8: 'הצגת יזמים',
  9: 'בחירת יזם',
  10: 'חתימות דיירים – הסכם שכר טרחה עו"ד',
  11: 'חתימות דיירים – הודעת בחירת יזם'
};

/** Hebrew name of a stage type; a type this table doesn't know yet still reads "שלב N". */
export const getStageName = (type?: number): string =>
  (type != null && STAGE_NAMES[type]) || (type != null ? `שלב ${type}` : '');

/**
 * The stages run in parallel tracks. 1→2→3 come first; once the lawyer is selected (3) the lawyer fee agreement
 * signatures (10, which gates the entrepreneurs 8→9 and then the entrepreneur notice 11), the inspector (5) and the
 * appraiser (7) run side by side.
 */
export interface StageTrack {
  id: string;
  title: string;
  stageTypes: number[];
  /** Shown while none of the track's stages exists yet. */
  opensAfter?: string;
}

export const STAGE_TRACKS: StageTrack[] = [
  { id: 'opening', title: 'פתיחת הפרויקט ובחירת עורך דין', stageTypes: [1, 2, 3] },
  { id: 'signatures', title: 'הסכם שכר טרחה עו"ד ובחירת יזם', stageTypes: [10, 8, 9, 11], opensAfter: 'ייפתח עם בחירת עורך הדין' },
  { id: 'inspector', title: 'מפקח', stageTypes: [5], opensAfter: 'ייפתח עם בחירת עורך הדין' },
  { id: 'appraiser', title: 'שמאי', stageTypes: [7], opensAfter: 'ייפתח עם בחירת עורך הדין' }
];

/** Stage types in display order: track by track. */
const STAGE_DISPLAY_ORDER: number[] = STAGE_TRACKS.reduce((order: number[], track) => order.concat(track.stageTypes), []);

const stageOrderIndex = (type?: number): number => {
  const index = STAGE_DISPLAY_ORDER.indexOf(type ?? -1);
  return index === -1 ? STAGE_DISPLAY_ORDER.length + (type ?? 0) : index;
};

export const sortStagesByTrack = <T extends { id?: number }>(stages: T[]): T[] =>
  [...stages].sort((a, b) => stageOrderIndex(a.id) - stageOrderIndex(b.id));

/** A project is complete once stages 10, 11, 5 and 7 all have `completedAt` (as the server's completion check). */
export const COMPLETION_STAGE_TYPES = [10, 11, 5, 7];

export const isProjectComplete = (stages: Array<{ id?: number; type?: number; completedAt?: string | null }> = []): boolean =>
  COMPLETION_STAGE_TYPES.every(type => stages.some(stage => (stage.type ?? stage.id) === type && !!stage.completedAt));

const normalizeProject = (item: ProjectsApiItem): Project => {
  const id = coerceNumber(item.id) ?? 0;
  
  // The building's address: street and number, city ("ביאליק 7, רמת גן") — several buildings share a street.
  const streetAndNumber = [item.street, item.buildingNumber]
    .map(part => (part == null ? '' : String(part).trim()))
    .filter(Boolean)
    .join(' ');
  const name = [streetAndNumber, item.settlementName?.trim()].filter(Boolean).join(', ') || `פרויקט #${id}`;

  let currentStageName = item.currentStage?.name;
  if (!currentStageName && item.currentStage?.type) {
    currentStageName = getStageName(item.currentStage.type);
  }

  const currentStageStartedAt = item.currentStage?.startedAt ?? undefined;
  const usersCount = coerceNumber(item.apartmentsCount);
  const createdAt = item.created;
  const currentStageId = item.currentStage?.type;

  // The open stages (the server returns only stages that were opened), in track order: several run in parallel.
  const stages = sortStagesByTrack((item.stages || []).map(normalizeStage));
  let activeStageNames: string[] = stages.filter(stage => !stage.completedAt).map(stage => stage.name);

  // Fallback if no stages array, use currentStage
  if (activeStageNames.length === 0 && currentStageName) {
      activeStageNames = [currentStageName];
  }

  return {
    id,
    name,
    title: name,
    createdAt: createdAt ?? undefined,
    currentStageId,
    currentStageName,
    activeStageNames,
    currentStageStartedAt,
    usersCount,
    stages,
    isCompleted: isProjectComplete(stages)
  };
};

function normalizeStage(item: ProjectStageApiItem): ProjectStage {
  // Named from the type only: the stages carry no name of their own.
  const name = getStageName(item.type);

  return {
    id: item.type,
    name,
    startedAt: item.startedAt ?? undefined,
    completedAt: item.completedAt ?? null,
    candidates: item.candidates || []
  };
}

const buildTenantFullName = (firstName?: string, lastName?: string): string => {
  const tokens = [firstName, lastName].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
  if (tokens.length) {
    return tokens.join(' ').trim();
  }
  return '';
};

const normalizeTenant = (item: ProjectUserApiItem, index: number): ProjectTenant => {
  const apartment = item.apartmentNumber != null ? String(item.apartmentNumber) : undefined;
  const phoneNumber = typeof item.phoneNumber === 'string' ? item.phoneNumber : (typeof item.phone === 'string' ? item.phone : undefined);
  const signedAt = item.claimed ?? item.signedAt ?? null;
  const hasSigned = typeof item.hasSigned === 'boolean' ? item.hasSigned : undefined;

  return {
    id: coerceNumber(item.id ?? item.userId) ?? index + 1,
    fullName: buildTenantFullName(item.firstName, item.lastName),
    firstName: item.firstName,
    lastName: item.lastName,
    idNumber: item.idNumber != null ? String(item.idNumber) : (item.identityNumber != null ? String(item.identityNumber) : undefined),
    email: typeof item.email === 'string' ? item.email : undefined,
    apartmentNumber: apartment,
    phoneNumber,
    signatureStatus: (hasSigned ?? Boolean(signedAt)) ? 'signed' : 'not-signed',
    hasSigned,
    signedAt,
    invitationCode: item.invitationCode,
    isRepresentative: item.isRepresentative,
    claimedBy: item.claimedBy != null ? coerceNumber(item.claimedBy) : null
  };
};

const buildErrorMessage = (message: string, error: unknown): string => {
  if (error instanceof ProjectsServiceError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return `${message}: ${error.message}`;
  }
  if (typeof error === 'string' && error.trim()) {
    return `${message}: ${error}`;
  }
  return `${message}: Unknown error`;
};

const handleServiceError = (code: string, message: string, error: unknown): never => {
  // A 401 already sent the admin to the login screen: pass its marker through untouched.
  if (error && typeof error === 'object' && 'isUnauthorized' in error) {
    throw error;
  }
  const msg = buildErrorMessage(message, error); 
  throw new ProjectsServiceError(code, msg, (error as any)?.response);
};

/** The documents' Hebrew names, as the server names them (Documents.GetHebrewName). */
export const DOCUMENT_NAMES = {
  hasmacha: 'כתב הסמכה',
  lawyerFeeAgreement: 'הסכם שכר טרחה עו"ד',
  entrepreneurNotice: 'הודעת בחירת יזם'
};

/**
 * The file name in a Content-Disposition header. Prefers the RFC 5987 `filename*=UTF-8''…` form — the Hebrew name,
 * percent-encoded — over the plain `filename="…"`, which the server fills with an ASCII fallback (Hebrew letters
 * turned into underscores).
 */
export const parseContentDispositionFileName = (disposition?: string | null): string | null => {
  if (!disposition) return null;

  const extended = /filename\*\s*=\s*([^;]+)/i.exec(disposition);
  if (extended) {
    // charset'language'value — the language is usually empty.
    const value = extended[1].trim().replace(/^"(.*)"$/, '$1');
    const parts = /^([^']*)'[^']*'(.*)$/.exec(value);
    const encoded = parts ? parts[2] : value;
    try {
      const decoded = decodeURIComponent(encoded).trim();
      if (decoded) return decoded;
    } catch {
      // Malformed encoding: fall back to the plain parameter.
    }
  }

  const plain = /filename\s*=\s*(?:"((?:[^"\\]|\\.)*)"|([^;]+))/i.exec(disposition);
  const name = plain ? (plain[1] ?? plain[2] ?? '').replace(/\\(.)/g, '$1').trim() : '';
  return name || null;
};

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * Downloads one of a project's documents (admin). The server names the file after the document and the building in
 * Content-Disposition; a document the building doesn't have yet answers 404 DOCUMENT_NOT_GENERATED — the error keeps
 * the response (its Blob body read back into JSON) so the screen can tell.
 */
const downloadProjectFile = async (
  path: string,
  projectId: number,
  documentName: string,
  errorCode: string
): Promise<{ blob: Blob; fileName: string }> => {
  try {
    const response = await http.get<any>(path, {
      responseType: 'blob',
      getFullResponse: true
    } as any);

    const blob: Blob = response.data;
    const disposition: string = response.headers?.['content-disposition'] ?? '';
    const contentType: string = response.headers?.['content-type'] ?? blob.type ?? '';
    const extension = contentType.split(';')[0].trim().toLowerCase() === 'application/pdf' ? 'pdf' : 'docx';
    const fileName = parseContentDispositionFileName(disposition) ?? `${documentName} - פרויקט ${projectId}.${extension}`;

    return { blob: blob.type ? blob : new Blob([blob], { type: contentType || DOCX_MIME }), fileName };
  } catch (error) {
    await parseBlobErrorBody(error);
    return handleServiceError(errorCode, `Failed to download ${path}`, error);
  }
};

const downloadProjectDocument = (
  projectId: number,
  documentPath: string,
  documentName: string,
  errorCode: string
): Promise<{ blob: Blob; fileName: string }> =>
  downloadProjectFile(`${PROJECTS_ENDPOINT}/${projectId}/document/${documentPath}`, projectId, documentName, errorCode);

const ProjectsService: ProjectsServiceType = {
  async getAllProjects(
    pageNumber: number = 1,
    pageSize: number = 10,
    searchTerm: string = ''
  ): Promise<ProjectsResponse> {
    try {
      const payload = await http.get<ProjectsApiResponse>(`${PROJECTS_ENDPOINT}/all`);
      
      const rawItems = (Array.isArray(payload) ? payload : payload?.data) ?? [];
      const items = rawItems.map(normalizeProject);
      const totalCount = items.length;

      return {
        items,
        pageNumber: 1,
        pageSize: totalCount,
        totalCount,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
        searchTerm
      };
    } catch (error) {
      return handleServiceError('FETCH_PROJECTS_ERROR', 'Failed to fetch projects', error);
    }
  },

  // There's no admin GET /Projects/{id}: one project comes from the list (e.g. a status page opened by URL).
  async getProjectById(id: number): Promise<Project | null> {
    const { items } = await ProjectsService.getAllProjects();
    return items.find(project => project.id === id) ?? null;
  },

  async deleteProject(id: number): Promise<{ success: true; message: string }> {
    try {
      // API returns { meta: { ... }, data: null }. The projects list toasts a refusal itself.
      await http.delete<any>(`${PROJECTS_ENDPOINT}/${id}`, null, { silentErrors: true });
      return {
        success: true,
        message: 'Project deleted successfully'
      };
    } catch (error) {
      return handleServiceError('DELETE_PROJECT_ERROR', `Failed to delete project ${id}`, error);
    }
  },

  async getProjectStages(projectId: number): Promise<ProjectStage[]> {
    try {
      const payload = await http.get<ProjectStagesApiResponse>(`${PROJECTS_ENDPOINT}/${projectId}/stages`);
      // Check for new data structure 'data' array, fallback to 'items', fallback to payload being array itself
      const rawItems = payload.data ?? payload.items ?? (Array.isArray(payload) ? payload : []);
      return (rawItems ?? []).map(normalizeStage);
    } catch (error) {
      return handleServiceError('FETCH_PROJECT_STAGES_ERROR', `Failed to fetch stages for project ${projectId}`, error);
    }
  },

  async getProjectResidents(projectId: number): Promise<TenantsResponse> {
    try {
      // Two endpoints, each with half of what the table needs:
      //   /residents — every apartment, including ones nobody has joined yet,
      //                with the signature status; no ID number or email.
      //   /users     — only people who created an account, with ID number and
      //                email; no signature status.
      // /residents is the roster; each row's `claimedBy` is the id of the user
      // who joined with it, which is how the ID number and email are attached.
      // If /users fails the table still loads, just without those two columns.
      const [residentsPayload, usersPayload] = await Promise.all([
        http.get<ProjectUsersApiResponse>(`${PROJECTS_ENDPOINT}/${projectId}/residents`),
        http.get<ProjectUsersApiResponse>(`${PROJECTS_ENDPOINT}/${projectId}/users`).catch(() => null),
      ]);
      const usersById = new Map(
        unwrapRows(usersPayload).map((user) => [coerceNumber(user.id), user] as const)
      );
      const rawItems = unwrapRows(residentsPayload).map((resident) => {
        const user = resident.claimedBy != null ? usersById.get(coerceNumber(resident.claimedBy)) : undefined;
        return user ? { ...resident, idNumber: user.idNumber, email: user.email } : resident;
      });
      const normalized = rawItems.map((item, index) => normalizeTenant(item, index));
      
      const totalCount = normalized.length;

      return {
        items: normalized,
        pageNumber: 1,
        pageSize: totalCount,
        totalCount: totalCount,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
        searchTerm: ''
      };
    } catch (error) {
       // Identify 404 error
       if (error && (error as any).response && (error as any).response.status === 404) {
           // Return empty structure instead of error
           return {
             items: [],
             pageNumber: 1,
             pageSize: 0,
             totalCount: 0,
             totalPages: 0,
             hasNextPage: false,
             hasPreviousPage: false,
             searchTerm: ''
           };
       }

      return handleServiceError('FETCH_PROJECT_TENANTS_ERROR', `Failed to fetch residents for project ${projectId}`, error);
    }
  },

  // The apartment a user joined in a project (its /residents row, by `claimedBy`): whether they signed the hasmacha
  // and are a representative. Null when no apartment of the project is theirs.
  async getUserApartment(projectId: number, userId: number): Promise<UserApartment | null> {
    try {
      const payload = await http.get<ProjectUsersApiResponse>(`${PROJECTS_ENDPOINT}/${projectId}/residents`);
      const row = unwrapRows(payload).find(resident => coerceNumber(resident.claimedBy) === userId);
      if (!row) {
        return null;
      }
      return {
        apartmentNumber: coerceNumber(row.apartmentNumber),
        hasSigned: Boolean(row.hasSigned),
        isRepresentative: Boolean(row.isRepresentative)
      };
    } catch (error) {
      return handleServiceError('FETCH_USER_APARTMENT_ERROR', `Failed to fetch the apartment of user ${userId}`, error);
    }
  },

  // Every apartment of the building, joined or not.
  async getProjectApartments(projectId: number): Promise<ProjectApartment[]> {
    try {
      const payload = await http.get<ProjectUsersApiResponse>(`${PROJECTS_ENDPOINT}/${projectId}/residents`);
      return unwrapRows(payload)
        .map(row => ({
          apartmentNumber: coerceNumber(row.apartmentNumber),
          claimedBy: coerceNumber(row.claimedBy),
          residentName: buildTenantFullName(row.firstName ?? undefined, row.lastName ?? undefined)
        }))
        .sort((a, b) => (a.apartmentNumber ?? 0) - (b.apartmentNumber ?? 0));
    } catch (error) {
      return handleServiceError('FETCH_PROJECT_APARTMENTS_ERROR', `Failed to fetch the apartments of project ${projectId}`, error);
    }
  },

  downloadHasmachaDocument(projectId: number): Promise<{ blob: Blob; fileName: string }> {
    return downloadProjectDocument(projectId, 'hasmacha', DOCUMENT_NAMES.hasmacha, 'DOWNLOAD_HASMACHA_ERROR');
  },

  downloadLawyerSignaturesDocument(projectId: number): Promise<{ blob: Blob; fileName: string }> {
    return downloadProjectDocument(projectId, 'lawyer-signatures', DOCUMENT_NAMES.lawyerFeeAgreement, 'DOWNLOAD_LAWYER_SIGNATURES_ERROR');
  },

  downloadEntrepreneurSignaturesDocument(projectId: number): Promise<{ blob: Blob; fileName: string }> {
    return downloadProjectDocument(projectId, 'entrepreneur-signatures', DOCUMENT_NAMES.entrepreneurNotice, 'DOWNLOAD_ENTREPRENEUR_SIGNATURES_ERROR');
  },

  // The five documents, whether each exists yet, and the signed copy the representative uploaded.
  async getProjectDocuments(projectId: number): Promise<ProjectDocumentInfo[]> {
    try {
      const payload = await http.get<any>(`${PROJECTS_ENDPOINT}/${projectId}/documents`);
      return unwrapRows(payload) as unknown as ProjectDocumentInfo[];
    } catch (error) {
      return handleServiceError('FETCH_PROJECT_DOCUMENTS_ERROR', `Failed to fetch the documents of project ${projectId}`, error);
    }
  },

  // Any of the five as docx or PDF, with the building's current data (404 DOCUMENT_NOT_GENERATED before the building
  // has it). PDFs share the admin's conversion limit (20 per 10 minutes): docx is the bulk format.
  downloadDocument(projectId: number, documentType: number, format: 'docx' | 'pdf', documentName: string): Promise<{ blob: Blob; fileName: string }> {
    return downloadProjectFile(`${PROJECTS_ENDPOINT}/${projectId}/documents/${documentType}/${format}`, projectId,
      documentName, 'DOWNLOAD_DOCUMENT_ERROR');
  },

  // The signed copy as uploaded (a PDF or a picture), named in Content-Disposition; 404 SIGNED_DOCUMENT_NOT_FOUND.
  downloadSignedDocument(projectId: number, documentType: number, documentName: string): Promise<{ blob: Blob; fileName: string }> {
    return downloadProjectFile(`${PROJECTS_ENDPOINT}/${projectId}/documents/${documentType}/signed`, projectId,
      `${documentName} - חתום`, 'DOWNLOAD_SIGNED_DOCUMENT_ERROR');
  },

  // How many apartments signed a document the residents sign (1–3). Null when it can't be read.
  async getSignatureProgress(projectId: number, documentType: number): Promise<DocumentSignatureProgress | null> {
    try {
      const payload = await http.get<any>(`${PROJECTS_ENDPOINT}/${projectId}/signatures/progress`, { params: { documentType } });
      const body = payload?.data ?? payload;
      return body && typeof body.signedCount === 'number' ? body as DocumentSignatureProgress : null;
    } catch (error) {
      if (error && typeof error === 'object' && 'isUnauthorized' in error) throw error;
      console.warn(`Failed to load the signatures of document ${documentType} for project ${projectId}`, error);
      return null;
    }
  },

  // The professional selected per profession (status 1): the survey winners, and the inspector and appraiser.
  async getSelectedProfessionals(projectId: number): Promise<ProjectProfessionalRow[]> {
    try {
      const payload = await http.get<any>(`${PROJECTS_ENDPOINT}/${projectId}/professionals`, { params: { status: 1 } });
      return unwrapRows(payload) as unknown as ProjectProfessionalRow[];
    } catch (error) {
      return handleServiceError('FETCH_PROJECT_PROFESSIONALS_ERROR', `Failed to fetch the professionals of project ${projectId}`, error);
    }
  }
};

export default ProjectsService;
